// src/services/DocumentService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { ClientDocument } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

type Document = ClientDocument;

const documentCollectionRef = collection(db, "documents");

export const DocumentService = {
  async createDocument(
    documentData: Omit<
      Document,
      "id" | "createdAt" | "updatedAt" | "users" | "children"
    >,
  ): Promise<string> {
    const newDocument: Omit<Document, "id"> = {
      ...documentData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const documentRef = await addDoc(documentCollectionRef, newDocument);
    return documentRef.id;
  },

  async getDocument(documentId: string): Promise<Document | null> {
    const documentDoc = await getDoc(doc(documentCollectionRef, documentId));
    if (documentDoc.exists()) {
      return { id: documentDoc.id, ...documentDoc.data() } as Document;
    }
    return null;
  },

  async getAllDocuments(accountId: string): Promise<Document[]> {
    const documentSnapshot = await getDocs(
      query(documentCollectionRef, where("accountId", "==", accountId)),
    );
    return documentSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Document,
    );
  },

  async updateDocument(
    documentId: string,
    updatedData: Partial<Omit<Document, "id" | "createdAt">>,
  ): Promise<void> {
    const documentDocRef = doc(documentCollectionRef, documentId);
    await updateDoc(documentDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteDocument(documentId: string): Promise<void> {
    const documentDocRef = doc(documentCollectionRef, documentId);
    await deleteDoc(documentDocRef);
  },
};
